import { Search, Users } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { DataTable } from '@/components/ui/data-table'
import { LoadingPage } from '@/components/ui/loading'
import { StatusBadge } from '@/components/ui/status-badge'
import { useAccounts } from '@/hooks/use-accounts'
import { formatNumber } from '@/lib/format'
import type { Account } from '@/lib/types'

const PER_PAGE = 20

const TIER_OPTIONS = ['all', 'free', 'starter', 'pro', 'growth', 'scale'] as const
const STATUS_OPTIONS = ['all', 'active', 'paused', 'suspended'] as const

export function AccountsPage(): React.JSX.Element {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [tier, setTier] = useState('all')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useAccounts({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    tier: tier === 'all' ? undefined : tier,
    status: status === 'all' ? undefined : status,
  })

  const columns = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (row: Account) => <span className="font-medium">{row.user_email}</span>,
    },
    {
      key: 'name',
      label: 'Name',
      render: (row: Account) => <span className="text-text-secondary">{row.user_name ?? '-'}</span>,
    },
    {
      key: 'tier',
      label: 'Tier',
      sortable: true,
      render: (row: Account) => <StatusBadge status={row.tier} />,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: Account) => <StatusBadge status={row.sending_paused ? 'paused' : row.status} />,
    },
    {
      key: 'emails',
      label: 'Emails Sent',
      sortable: true,
      render: (row: Account) => formatNumber(row.emails_sent_this_period),
    },
    {
      key: 'overrides',
      label: 'Overrides',
      render: (row: Account) =>
        row.overrides ? (
          <StatusBadge status="custom" />
        ) : (
          <span className="text-text-tertiary">-</span>
        ),
    },
    { key: 'domains', label: 'Domains', render: (row: Account) => row.domain_count },
    {
      key: 'created',
      label: 'Created',
      sortable: true,
      render: (row: Account) => new Date(row.created_at).toLocaleDateString(),
    },
  ]

  if (error) {
    return (
      <div className="space-y-6">
        <AccountsHeader />
        <div className="rounded-xl border border-danger/30 bg-surface-0 p-12 text-center">
          <p className="text-danger">Failed to load accounts. Please try again.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <AccountsHeader />

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="w-full rounded-lg border border-surface-3 bg-surface-0 py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
          />
        </div>
        <select
          value={tier}
          onChange={(e) => {
            setTier(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
        >
          {TIER_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t === 'all' ? 'All Tiers' : t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <LoadingPage message="Loading accounts..." />
      ) : (
        <DataTable
          columns={columns}
          data={data?.data ?? []}
          onRowClick={(row) => navigate(`/accounts/${row.id}`)}
          pagination={
            data ? { page: data.page, perPage: data.per_page, total: data.total } : undefined
          }
          onPageChange={setPage}
          emptyMessage="No accounts found matching your filters."
        />
      )}
    </div>
  )
}

function AccountsHeader(): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <Users className="h-6 w-6 text-admin-500" />
      <h1 className="text-2xl font-semibold text-text-primary">Accounts</h1>
    </div>
  )
}
